import { userModule } from '@/store/user/module'
import * as amplitude from '@amplitude/analytics-browser'
import { Experiment, type ExperimentConfig } from '@amplitude/experiment-js-client'
import { analyticsModule } from '@/store/analytics/module'
import { isSupportWindow } from '@/utils'
import type { RouteLocationNormalized } from 'vue-router'
import { licenseModule } from '@/store/license/module'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import * as Sentry from '@sentry/browser'
import { runCloudFunction } from '@/store/parseUtils'
import type { Study } from '@pocketprep/types'

const initAmplitude = async () => {
    const amplitudeApiKey = import.meta.env.VUE_APP_AMPLITUDE_API_KEY
    if (amplitudeApiKey && !isSupportWindow()) {
        const ampliInitConfig: amplitude.Types.BrowserOptions = {
            defaultTracking: {
                pageViews: false,
                sessions: true,
                formInteractions: false,
                fileDownloads: false,
                attribution: {
                    /**
                     * See ampli docs here
                     * https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#exclude-referrers
                     *  */
                    excludeReferrers: [],
                },
            },
        }
        const customAmpliServerUrl = import.meta.env.VUE_APP_AMPLITUDE_ANALYTICS_URL
        if (customAmpliServerUrl) {
            ampliInitConfig.serverUrl = customAmpliServerUrl
        }
        const replaySampleRate = import.meta.env.VUE_APP_AMPLITUDE_REPLAY_SAMPLE_RATE
        if (replaySampleRate) {
            const sessionReplayTracking = sessionReplayPlugin({
                sampleRate: Number(replaySampleRate) || 0,
            })
            amplitude.add(sessionReplayTracking)
        }
        amplitude.init(amplitudeApiKey, ampliInitConfig)
        const ampliExperimentConfig: ExperimentConfig = {}
        const customAmpliExperimentUrl = import.meta.env.VUE_APP_AMPLITUDE_EXPERIMENT_URL
        if (customAmpliExperimentUrl) {
            ampliExperimentConfig.serverUrl = customAmpliExperimentUrl
        }
        const customAmpliFlagUrl = import.meta.env.VUE_APP_AMPLITUDE_FLAG_URL
        if (customAmpliFlagUrl) {
            ampliExperimentConfig.flagsServerUrl = customAmpliFlagUrl
        }
        const experiment = Experiment.initializeWithAmplitudeAnalytics(amplitudeApiKey, ampliExperimentConfig)
        analyticsModule.state.experiment = experiment
        await experiment.start()
    }
}

const amplitudeFetchVariant = async (flagKey: string) => {
    const amplitudeApiKey = import.meta.env.VUE_APP_AMPLITUDE_API_KEY
    const experiment = analyticsModule.state.experiment
    if (amplitudeApiKey && !isSupportWindow() && experiment) {
        const user = userModule.state.user
        if (user) {
            await experiment.fetch({
                user_id: user.objectId,
                user_properties: {
                    email: user.username,
                    createdAt: user.createdAt,
                },
            }, {
                flagKeys: [ flagKey ],
            })
        } else {
            await experiment.fetch(undefined, { flagKeys: [ flagKey ] })
        }
        const variant = experiment.variant(flagKey)
        return variant
    }
}

const getAmplitudeSessionId = () => {
    return amplitude.getSessionId()
}

const getAmplitudeDeviceId = () => {
    return amplitude.getDeviceId()
}

const getAmplitudeUserId = () => {
    return amplitude.getUserId()
}

const amplitudeTrack = (
    eventName: string,
    eventProperties?: Record<string, string | number | boolean | string[] | undefined | Record<string, unknown>>
) => {
    // Build default event properties
    const clientVersion: string = import.meta.env.VUE_APP_VERSION
    const currentBundle = bundleModule.getters.getCurrentBundle()
    const currentExamMetadata = examMetadataModule.getters.getCurrentExamMetadata()
    const organizationId = userModule.getters.getCurrentOrganizationId() || undefined
    const userDomain = userModule.getters.getUserDomain()

    const defaultProperties = {
        bundleId: currentBundle?.objectId || undefined,
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion,
        examGuid: currentExamMetadata?.examGuid || undefined,
        organizationId,
        userDomain,
    }

    const amplitudeApiKey = import.meta.env.VUE_APP_AMPLITUDE_API_KEY
    if (amplitudeApiKey && !isSupportWindow()) {
        amplitude.track(eventName, {
            ...defaultProperties,
            ...eventProperties,
        })
    }
}

const amplitudeTrackScreen = (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    const screenName = analyticsModule.getters.getScreenName(to)

    // We do not want to call screen view when a user views quiz results after taking a quiz
    // ^This is duplicate of the 'Quiz Completed' Amplitude event
    // We do want to call scree view with 'Historical Quiz Results' if a user views a quiz coming from the Stats tab
    if (screenName === 'Quiz Results') {
        if (from.name === 'stats') {
            amplitudeTrack('Screen View', {
                screenName: 'Historical Quiz Results',
                platform: 'Web',
            })
        }
        return screenName
    }

    if (screenName) {
        amplitudeTrack('Screen View', {
            screenName,
            platform: 'Web',
        })
    }

    return screenName
}

const amplitudeReset = () => {
    const amplitudeApiKey = import.meta.env.VUE_APP_AMPLITUDE_API_KEY
    if (amplitudeApiKey && !isSupportWindow()) {
        amplitude.reset()
    }
}

const setAmplitudeSessionId = (id: string) => {
    if (!Number.isNaN(Number(id))) {
        amplitude.setSessionId(Number(id))
    }
}

const setAmplitudeUser = ({ id, email, createdAt }: {
    id: string
    email: string
    createdAt: string   // ISO date string
}) => {
    const amplitudeApiKey = import.meta.env.VUE_APP_AMPLITUDE_API_KEY
    if (amplitudeApiKey && !isSupportWindow()) {
        amplitude.setUserId(id)

        const mostRecentLicense = licenseModule.getters.getMostRecentLicense()
        const recentLicenseOrgId = mostRecentLicense?.organization?.objectId
        if (recentLicenseOrgId) {
            amplitude.setGroup('Organization', recentLicenseOrgId)
        }

        const identifyEvent = new amplitude.Identify()
        identifyEvent.set('email', email)
        identifyEvent.set('createdAt', createdAt)

        // clear out the Stripe Coupon Id if no referral
        const queryParams = Object.fromEntries(new URLSearchParams(window.location.search))
        if (!('referral' in queryParams)) {
            identifyEvent.unset('stripeCouponId')
        }
        amplitude.identify(identifyEvent)
    }
}

const bootIntercom = async () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        const intercomSettings = analyticsModule.getters.intercomSettings()
        const intercomUserJWT = await fetchIntercomJWT()
        window.Intercom('boot', {
            ...intercomSettings,
            ...(intercomUserJWT && { intercom_user_jwt: intercomUserJWT }),
        })
    }
}

const updateIntercom = async () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        const intercomSettings = analyticsModule.getters.intercomSettings()
        const intercomUserJWT = await fetchIntercomJWT()
        window.Intercom('update', {
            ...intercomSettings,
            ...(intercomUserJWT && { intercom_user_jwt: intercomUserJWT }),
        })
    }
}

// Intercom recommends generating a fresh JWT for every boot/update call, rather than caching it,
// since it should only need to live long enough to cover the current Messenger session.
const fetchIntercomJWT = async () => {
    try {
        // Can only fetch the JWT with a valid user
        const user = userModule.state.user
        if (!user) {
            return
        }

        return await runCloudFunction<Study.Cloud.fetchIntercomJWT>('fetchIntercomJWT')
    } catch (err) {
        Sentry.captureException(err)
    }
}

const googleAnalyticsTrack = async (eventName: string, eventProperties?: Record<string, string | number | boolean>) => {
    if (import.meta.env.VUE_APP_GOOGLE_ANALYTICS_ID && !isSupportWindow()) {
        gtag('event', eventName, {
            ...eventProperties,
        })
    }
    return Promise.resolve()
}

const trackEvent = async (eventName: string, eventProperties?: Record<string, string | number | boolean>) => {
    // Can send an event to multiple destinations, if needed
    await googleAnalyticsTrack(eventName, eventProperties)
}

const cioIdentify = ({ id, email }: {
    id: string
    email: string
}) => {
    if (import.meta.env.VUE_APP_CIO_WRITE_KEY && !isSupportWindow() && window.isSecureContext) {
        analyticsModule.state.customerio?.identify(id, {
            email,
        }).catch((err: unknown) => Sentry.captureException(err))
    }
}

const cioReset = () => {
    if (import.meta.env.VUE_APP_CIO_WRITE_KEY && !isSupportWindow() && window.isSecureContext) {
        analyticsModule.state.customerio?.reset().catch((err: unknown) => Sentry.captureException(err))
    }
}

const impactIdentify = ({ id, email }: {
    id: string
    email: string
}) => {
    if (import.meta.env.VUE_APP_IMPACT_UTT && !isSupportWindow()) {
        window.ire('identify', {
            customerId: email,
            customerEmail: email,
            customProfileId: id,
        })
    }
}

export default {
    initAmplitude,
    amplitudeFetchVariant,
    getAmplitudeSessionId,
    getAmplitudeDeviceId,
    getAmplitudeUserId,
    amplitudeTrack,
    amplitudeTrackScreen,
    setAmplitudeSessionId,
    setAmplitudeUser,
    amplitudeReset,
    bootIntercom,
    updateIntercom,
    googleAnalyticsTrack,
    trackEvent,
    cioIdentify,
    cioReset,
    impactIdentify,
}
